import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, fromBaseCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import { toastApiError } from "../lib/apiErrors";
import axios from "axios";
import { toast } from "sonner";
import { UsersRound, Plus, Pencil, Trash2, History, Search, Wallet, HandCoins } from "lucide-react";
import { ReceiptActions, StatementActions } from "../components/customers/ReceiptActions";
import { TPASelector, useTPAs } from "../components/payments/TPASelector";

const emptyForm = { name: "", nif: "", phone: "", email: "", address: "", notes: "", credit_limit: "" };

export default function CustomersPage() {
  const { token, isManager } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [historyModal, setHistoryModal] = useState(false);
  const [history, setHistory] = useState(null);
  const [accountModal, setAccountModal] = useState(false);
  const [account, setAccount] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payTpaId, setPayTpaId] = useState("");
  const [lastReceipt, setLastReceipt] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [settings, setSettings] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const tpas = useTPAs(headers);

  useEffect(() => {
    fetchCustomers();
    axios.get(`${API_URL}/settings`, { headers, withCredentials: true }).then((r) => setSettings(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/customers`, { headers, withCredentials: true });
      setCustomers(res.data);
    } catch { toast.error("Erro ao carregar clientes"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, credit_limit: toBaseCurrency(form.credit_limit) };
      if (editing) {
        await axios.put(`${API_URL}/customers/${editing.customer_id}`, payload, { headers, withCredentials: true });
        toast.success("Cliente atualizado");
      } else {
        await axios.post(`${API_URL}/customers`, payload, { headers, withCredentials: true });
        toast.success("Cliente criado");
      }
      setModal(false); setEditing(null); setForm(emptyForm);
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao guardar"); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remover cliente "${c.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/customers/${c.customer_id}`, { headers, withCredentials: true });
      toast.success("Cliente removido");
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const openHistory = async (c) => {
    try {
      const res = await axios.get(`${API_URL}/customers/${c.customer_id}/history`, { headers, withCredentials: true });
      setHistory(res.data);
      setHistoryModal(true);
    } catch { toast.error("Erro ao carregar histórico"); }
  };

  const openAccount = async (c, keepReceipt = false) => {
    try {
      const [res, rcs] = await Promise.all([
        axios.get(`${API_URL}/customers/${c.customer_id}/account`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/customers/${c.customer_id}/receipts`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAccount(res.data);
      setReceipts(rcs.data || []);
      setPayAmount("");
      if (!keepReceipt) setLastReceipt(null);
      setAccountModal(true);
    } catch { toast.error("Erro ao carregar conta corrente"); }
  };

  const registerPayment = async (e) => {
    e.preventDefault();
    if (payMethod === "card" && tpas.length && !payTpaId) return toast.error("Selecione o TPA onde o cartão foi passado");
    try {
      const res = await axios.post(`${API_URL}/customers/${account.customer.customer_id}/payments`, {
        amount: toBaseCurrency(payAmount),
        method: payMethod,
        tpa_id: payMethod === "card" && payTpaId ? payTpaId : null,
      }, { headers, withCredentials: true });
      toast.success(`Recebimento registado. Novo saldo: ${formatCurrency(res.data.customer_balance)}`);
      setLastReceipt(res.data.receipt || null);
      openAccount(account.customer, true);
    } catch (err) { toastApiError(err, "Erro no recebimento"); }
  };

  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) || (c.nif || "").includes(search)
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Clientes</h1>
          <p className="text-muted-foreground">Ficha de clientes e histórico de compras (CRM)</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setModal(true); }} data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Pesquisar por nome ou NIF..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search-input" />
      </div>

      {loading ? (
        <p className="text-muted-foreground">A carregar...</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <UsersRound className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nenhum cliente encontrado
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <Card key={c.customer_id} data-testid={`customer-card-${c.customer_id}`}>
              <CardContent className="p-4 space-y-2">
                <p className="font-semibold">{c.name}</p>
                {c.nif && <p className="text-xs text-muted-foreground">NIF: {c.nif}</p>}
                {c.phone && <p className="text-sm text-muted-foreground">{c.phone}</p>}
                {c.email && <p className="text-sm text-muted-foreground">{c.email}</p>}
                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openAccount(c)} data-testid={`customer-account-${c.customer_id}`}>
                    <Wallet className="w-4 h-4 mr-1" /> Conta
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openHistory(c)} data-testid={`customer-history-${c.customer_id}`}>
                    <History className="w-4 h-4 mr-1" /> Histórico
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(c); setForm({ ...emptyForm, ...c, credit_limit: c.credit_limit ? fromBaseCurrency(c.credit_limit).toString() : "" }); setModal(true); }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {isManager && (
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(c)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar Cliente" : "Novo Cliente"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>NIF</Label>
                <Input value={form.nif || ""} onChange={(e) => setForm({ ...form, nif: e.target.value })} data-testid="customer-nif-input" />
                <p className="text-xs text-muted-foreground">Liga as faturas emitidas com este NIF</p>
              </div>
              <div className="space-y-1">
                <Label>Telefone</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Endereço</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Limite de Crédito</Label>
              <Input type="number" step="0.01" min="0" value={form.credit_limit || ""} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder="0 = sem limite definido" data-testid="customer-credit-limit-input" />
              <p className="text-xs text-muted-foreground">Vendas a crédito são bloqueadas quando o saldo ultrapassa este limite</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-customer-btn">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={accountModal} onOpenChange={setAccountModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="pr-6 break-words">Conta Corrente — {account?.customer?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Saldo em Dívida</p>
                <p className={`font-bold font-mono ${(account?.balance || 0) > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="customer-balance">
                  {formatCurrency(account?.balance)}
                </p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Limite</p>
                <p className="font-bold font-mono">{account?.credit_limit ? formatCurrency(account.credit_limit) : "—"}</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Disponível</p>
                <p className="font-bold font-mono">{account?.available_credit != null ? formatCurrency(account.available_credit) : "—"}</p>
              </CardContent></Card>
            </div>
            {account?.over_limit && (
              <p className="text-sm font-semibold text-red-600">⚠ Este cliente excedeu o limite de crédito — novas vendas a crédito estão bloqueadas.</p>
            )}

            {account?.customer && <StatementActions customerId={account.customer.customer_id} customerEmail={account.customer.email} />}

            <form onSubmit={registerPayment} className="space-y-2 p-3 rounded-lg border bg-emerald-500/5">
              <Label className="text-xs">Registar Recebimento</Label>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <Input type="number" step="0.01" min="0.01" required value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="Valor recebido" className="flex-1 min-w-0" data-testid="customer-payment-input" />
                <select className="h-10 rounded-md border bg-background px-2 text-sm" value={payMethod} onChange={(e) => { setPayMethod(e.target.value); setPayTpaId(tpas.length === 1 ? tpas[0].tpa_id : ""); }} data-testid="customer-payment-method">
                  <option value="cash">Numerário</option>
                  <option value="transfer">Transferência</option>
                  <option value="card">Cartão (TPA)</option>
                </select>
                <Button type="submit" data-testid="register-payment-btn"><HandCoins className="w-4 h-4 mr-1" /> Receber</Button>
              </div>
              {payMethod === "card" && <TPASelector tpas={tpas} value={payTpaId} onChange={setPayTpaId} />}
            </form>

            {lastReceipt && <ReceiptActions receipt={lastReceipt} settings={settings || {}} customerEmail={account?.customer?.email} />}

            {receipts.length > 0 && (
              <div className="space-y-2" data-testid="customer-receipts-list">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Recibos (RC)</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {receipts.map((rc) => (
                    <div key={rc.document_id} className="flex flex-wrap justify-between items-center gap-2 p-2 rounded border text-sm" data-testid={`customer-receipt-${rc.document_id}`}>
                      <div>
                        <p className="font-mono font-medium">{rc.document_number}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(rc.created_at)} · {formatCurrency(rc.total)}</p>
                      </div>
                      <ReceiptActions receipt={rc} settings={settings || {}} customerEmail={account?.customer?.email} compact />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="max-h-56 overflow-y-auto space-y-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Extrato</p>
              {(account?.ledger || []).map((e) => (
                <div key={e.entry_id} className="flex justify-between items-center p-2 rounded border text-sm">
                  <div>
                    <p className="font-medium">{e.description}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(e.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-bold ${e.type === "debit" ? "text-red-600" : "text-emerald-600"}`}>
                      {e.type === "debit" ? "+" : "-"}{formatCurrency(e.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">saldo: {formatCurrency(e.balance_after)}</p>
                  </div>
                </div>
              ))}
              {(account?.ledger || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-3">Sem movimentos na conta corrente</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyModal} onOpenChange={setHistoryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Histórico — {history?.customer?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Total Gasto</p>
                <p className="font-bold font-mono" data-testid="customer-total-spent">{formatCurrency(history?.total_spent)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Nº Faturas</p>
                <p className="font-bold">{history?.invoice_count || 0}</p>
              </CardContent></Card>
            </div>
            {!history?.customer?.nif && (
              <p className="text-xs text-amber-600">Este cliente não tem NIF — adicione o NIF para associar as faturas.</p>
            )}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {(history?.invoices || []).map((inv) => (
                <div key={inv.invoice_id} className="flex justify-between items-center p-2 rounded border text-sm">
                  <div>
                    <p className="font-mono font-medium">{inv.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(inv.created_at)}</p>
                  </div>
                  <p className="font-mono">{formatCurrency(inv.total)}</p>
                </div>
              ))}
              {(history?.invoices || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sem faturas associadas</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
